import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import './toolkit1.css';
//import ChatBot from './ChatBot';

const Toolkit1 = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [strategicAreas, setStrategicAreas] = useState([]);
  const [thematicAreas, setThematicAreas] = useState([]);
  const [filteredThematicAreas, setFilteredThematicAreas] = useState([]);
  const [selectedStrategicArea, setSelectedStrategicArea] = useState('');
  const [selectedThematicArea, setSelectedThematicArea] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showChatbot, setShowChatbot] = useState(false);
  const [showGlobalChatbot, setShowGlobalChatbot] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of items to show per "page"
  const observerTarget = React.useRef(null);

  useEffect(() => {
    const readExcel = async () => {
      const file = await fetch('/toolkit.xlsx');
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);
      
      const sortedData = [...jsonData].sort((a, b) => {
        const titleA = (a.Title || '').toLowerCase();
        const titleB = (b.Title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      });
      
      setData(sortedData);
      setFilteredData(sortedData);
      
      const uniqueStrategicAreas = [...new Set(jsonData.map(item => item['Strategic Area']))];
      const uniqueThematicAreas = [...new Set(jsonData.map(item => item['Thematic Area']))];
      setStrategicAreas(uniqueStrategicAreas);
      setThematicAreas(uniqueThematicAreas);
    };
    readExcel();
  }, []);

  // Add useEffect to handle filtering when either dropdown selection changes
  useEffect(() => {
    handleFilter();
  }, [selectedStrategicArea, selectedThematicArea]);

  const handleFilter = () => {
    let filtered = [...data];
    
    if (selectedStrategicArea) {
      filtered = filtered.filter(item => item['Strategic Area'] === selectedStrategicArea);
    }
    
    if (selectedThematicArea) {
      filtered = filtered.filter(item => item['Thematic Area'] === selectedThematicArea);
    }
    
    const sortedFiltered = filtered.sort((a, b) => {
      const titleA = (a.Title || '').toLowerCase();
      const titleB = (b.Title || '').toLowerCase();
      return titleA.localeCompare(titleB);
    });
    
    setFilteredData(sortedFiltered);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedStrategicArea('');
    setSelectedThematicArea('');
    setFilteredData(data);
    setFilteredThematicAreas([]);
    setCurrentPage(1);
  };
  const truncateText = (text, wordLimit) => {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length > wordLimit) {
      return words.slice(0, wordLimit).join(' ') + '...';
    }
    return text;
  };
  const getPaginatedData = () => {
    return filteredData.slice(0, currentPage * itemsPerPage);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && getPaginatedData().length < filteredData.length) {
          setCurrentPage(prevPage => prevPage + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [filteredData]);

  return (
    <div className="toolkit-container">
      <h1>Search Commonwealth Toolkits</h1>
      <div className="filters-section">
        <div className="filter-group">
          <select
            value={selectedStrategicArea}
            onChange={(e) => {
              const newStrategicArea = e.target.value;
              setSelectedStrategicArea(newStrategicArea);
              
              if (newStrategicArea) {
                const relatedThematicAreas = [...new Set(
                  data.filter(item => item['Strategic Area'] === newStrategicArea)
                    .map(item => item['Thematic Area'])
                )];
                setFilteredThematicAreas(relatedThematicAreas);
              } else {
                setFilteredThematicAreas([]);
              }
              setSelectedThematicArea('');
            }}
          >
            <option value="">Select Strategic Area</option>
            {strategicAreas.map((area, index) => (
              <option key={index} value={area}>{area}</option>
            ))}
          </select>
          <select
            value={selectedThematicArea}
            onChange={(e) => {
              setSelectedThematicArea(e.target.value);
            }}
            disabled={!selectedStrategicArea}
          >
            <option value="">Select Thematic Area</option>
            {selectedStrategicArea && filteredThematicAreas.map((area, index) => (
              <option key={index} value={area}>{area}</option>
            ))}
          </select>
          <button onClick={clearFilters}>Clear</button>
        </div>
      </div>
      <div className={`results-section ${isModalOpen ? 'blur' : ''}`}>
        {getPaginatedData().map((item, index) => (
          <div key={index} className="result-card">
            <h2>{item.Title || 'No Title Available'}</h2>
            <div className="card-content">
              <div className="left-column">
                <p><strong>Department:</strong> {item['Department']}</p>
                <p><strong>Strategic Area:</strong> {item['Strategic Area']}</p>
                <p><strong>Thematic Area:</strong> {item['Thematic Area']}</p>
                <p><strong>Synopsis:</strong> {truncateText(item['Synopsis/Abstract'], 75)}
                  {item['Synopsis/Abstract']?.split(' ').length > 75 && (
                    <button 
                      className="read-more-link"
                      onClick={() => {
                        setSelectedItem(item);
                        setIsModalOpen(true);
                      }}
                    >
                      Read more
                    </button>
                  )}
                </p>
              </div>
              <div className="right-column">
                <button 
                  className="more-info"
                  onClick={() => {
                    setSelectedItem(item);
                    setIsModalOpen(true);
                  }}
                >
                  More info
                </button>
              </div>
            </div>
          </div>
        ))}
        <div ref={observerTarget} style={{ height: '20px' }}></div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <button className="close-button" onClick={() => setIsModalOpen(false)}>
              ×
            </button>
            <h2>{selectedItem?.Title || 'No Title Available'}</h2>
            <div className="modal-body">
              <p><strong>Department:</strong> {selectedItem?.Department}</p>
              <p><strong>Strategic Area:</strong> {selectedItem?.['Strategic Area']}</p>
              <p><strong>Thematic Area:</strong> {selectedItem?.['Thematic Area']}</p>
              <p><strong>Synopsis:</strong> {selectedItem?.['Synopsis/Abstract']}</p>
              <div className="button-container">
                <a href={selectedItem?.Link} target="_blank" rel="noopener noreferrer" className="link-button">
                  View as PDF
                </a>
                {/* TODO: Add a team email column in excel file */}
                <a href={`mailto:${selectedItem?.['Team Email']}`} className="link-button">
                  Contact Team
                </a>
                {/* <button className="link-button" onClick={() => setShowChatbot(true)}>
                  Chat with AI
                </button> */}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render ChatBot component when showChatbot is true 
      {showChatbot && (
        <ChatBot 
          isOpen={showChatbot}
          onClose={() => setShowChatbot(false)}
          selectedDocument={selectedItem}
        />
      )}

      {/* Render global ChatBot component when showGlobalChatbot is true 
      {showGlobalChatbot && (
        <ChatBot
          isOpen={showGlobalChatbot}
          onClose={() => setShowGlobalChatbot(false)}
          selectedDocument={null}
        />
      )}

      {/* Global chat button that opens the global chatbot 
      <button 
        className="global-chat-button"
        onClick={() => setShowGlobalChatbot(true)}
      >
        <span className="chat-icon">💬</span>
      </button>*/}
    </div>
  );
};

export default Toolkit1;
